import type { ApiPendingAddressResponse, ApiAddressRecord, FormBody } from '../dto/postOrderAddress.dto';
import type { PendingAddressData, AddressDetails, AddressSubmission } from '../../domain/models/postOrderAddress.types';

const isPlaceholder = (val?: string) => {
  if (!val) return true;
  const normalized = val.toLowerCase().trim();
  return (
    normalized.includes('to be updated') || 
    normalized === 'pending' || 
    normalized === 'na' ||
    normalized === 'n/a'
  );
};

const mapAddress = (addr?: ApiAddressRecord): AddressDetails => ({
  addressId: addr?.addressId ? Number(addr.addressId) : undefined,
  fullName: addr?.fullName || '',
  contactNumber: addr?.contactNumber || '',
  postCode: addr?.postCode || '',
  cityName: addr?.cityName || '',
  stateId: addr?.state?.id?.toString() || '',
  landmark: addr?.landmark || '',
  address: isPlaceholder(addr?.address) ? '' : (addr?.address || ''),
  street: isPlaceholder(addr?.street) ? '' : (addr?.street || ''),
  locality: isPlaceholder(addr?.locality) ? '' : (addr?.locality || ''),
  countryId: '101', 
});

const flattenAddressData = (addr: AddressDetails, prefix: string): FormBody => {
  const flat: FormBody = {};
  const p = `${prefix}_`;
  const fields: (keyof AddressDetails)[] = [
    'fullName', 'contactNumber', 'postCode', 'address', 'street', 
    'locality', 'landmark', 'cityName', 'stateId', 'alternateContactNumber', 'countryId'
  ];
  
  for (const key of fields) {
    if (addr[key] !== undefined && addr[key] !== null) {
      flat[`${p}${key}`] = String(addr[key]);
    }
  }
  return flat;
};

export const adaptPendingAddressToDomain = (data: ApiPendingAddressResponse): PendingAddressData => ({
  orderCode: data.orderCode,
  orderToken: data.orderToken,
  sameAddressRecord: data.sameAddressRecord,
  stateOptions: Object.entries(data.stateIdVsNameMap || {}).map(([id, name]) => ({ id, name })),
  shippingAddress: mapAddress(data.shippingAddress),
  billingAddress: mapAddress(data.billingAddress),
  orderItems: (data.orderItems || []).map(item => ({
    orderItemCode: item.orderItemCode || '',
    productName: item.productName || '',
    productUrl: item.productUrl || '',
    imageUrl: item.imageUrl || '',
    sellingPrice: Number(item.sellingPrice) || 0,
    expectedDeliveryDate: item.expectedDeliveryDate || '',
  })),
});

export const flattenAddressForApi = (submission: AddressSubmission): FormBody => {
  const shippingData = flattenAddressData(submission.shippingAddress, 'shipping');
  const billingData = flattenAddressData(
    submission.sameAsShipping ? submission.shippingAddress : submission.billingAddress, 
    'billing'
  );
  
  const baseData: FormBody = {};
  if (submission.orderToken) {
    baseData.orderToken = submission.orderToken;
  } else if (submission.orderCode) {
    baseData.orderCode = submission.orderCode;
  }
  
  return { ...shippingData, ...billingData, ...baseData };
};